import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";

dayjs.extend(isoWeek);
dayjs.extend(utc);

export type UsageLimitationTimeframe = "day" | "week" | "all";

type UsageLimitation = {
  timeframe: UsageLimitationTimeframe;
  limitAmount: number;
};

type UsageCandidate = {
  startTimeUtc: Date | string;
  countsTowardSubscriptionUsage: boolean;
  // Billing period the appointment falls in; undefined when it falls in none.
  periodKey: string | undefined;
};

export type LimitationUsage = {
  timeframe: UsageLimitationTimeframe;
  used: number;
  limitAmount: number | null;
};

const DISPLAY_PRIORITY: UsageLimitationTimeframe[] = ["all", "week", "day"];

function pickDisplayedLimitation(limitations: UsageLimitation[]): {
  timeframe: UsageLimitationTimeframe;
  limitAmount: number | null;
} {
  for (const timeframe of DISPLAY_PRIORITY) {
    const limitation = limitations.find(
      (candidate) => candidate.timeframe === timeframe,
    );
    if (limitation) return limitation;
  }
  return { timeframe: "all", limitAmount: null };
}

// Buckets in UTC with Monday-start weeks to match how validateSubscriptionUse
// counts a limitation on the server, so the number shown is the one enforced.
function getBucketKey(
  timeframe: UsageLimitationTimeframe,
  candidate: UsageCandidate,
): string {
  switch (timeframe) {
    case "day":
      return `day:${dayjs.utc(candidate.startTimeUtc).startOf("day").valueOf()}`;
    case "week":
      return `week:${dayjs.utc(candidate.startTimeUtc).startOf("isoWeek").valueOf()}`;
    case "all":
      return `all:${candidate.periodKey ?? "no-period"}`;
  }
}

// Same order as `appointments`; null where the appointment does not count toward usage.
export function numberSubscriptionAppointmentUsage({
  appointments,
  limitations,
}: {
  appointments: UsageCandidate[];
  limitations: UsageLimitation[];
}): (LimitationUsage | null)[] {
  const { timeframe, limitAmount } = pickDisplayedLimitation(limitations);
  const counter = new Map<string, number>();
  const usages: (LimitationUsage | null)[] = appointments.map(() => null);

  appointments
    .map((candidate, index) => ({ candidate, index }))
    .filter(({ candidate }) => candidate.countsTowardSubscriptionUsage)
    .toSorted(
      (a, b) =>
        dayjs(a.candidate.startTimeUtc).valueOf() -
        dayjs(b.candidate.startTimeUtc).valueOf(),
    )
    .forEach(({ candidate, index }) => {
      const key = getBucketKey(timeframe, candidate);
      const used = (counter.get(key) ?? 0) + 1;
      counter.set(key, used);
      usages[index] = { timeframe, used, limitAmount };
    });

  return usages;
}
